/* eslint-disable filename-rules/match */

import { storeApi, useAppSelector } from "store/index";
import { selectAvailableHomeRoles, selectCurrentUserId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { useLocation } from "utils/routing";
import { HomeRole, homeUrlForRole } from "utils/urls";

export const getDefaultHomeRole = (availableRoles: HomeRole[]) => {
  for (const role of [HomeRole.ADMIN, HomeRole.INSTRUCTOR, HomeRole.SECTION_ASSISTANT]) {
    if (availableRoles.includes(role)) return role;
  }

  return HomeRole.STUDENT;
};

/** Role segment from `/home/<role>`, or undefined on bare `/home` / unrelated paths. */
export const getHomeRoleFromPath = (pathname: string): HomeRole | undefined =>
  (Object.values(HomeRole) as HomeRole[]).find((role) => pathname.startsWith(homeUrlForRole(role)));

export const useHomeRole = () => {
  const location = useLocation();
  const userId = useAppSelector(selectCurrentUserId);

  // Enrollments + orgs: Admin home role needs current org (`org.current`) for canAuthorOrg.
  // Skip API calls if userId is undefined (user not logged in)
  const { isLoading: isHomeRoleLoading } = usePromiseEffect(() => {
    if (!userId) return Promise.resolve(undefined);

    return Promise.all([
      storeApi.orgs.list(),
      storeApi.section_users.list({ user_id: userId }),
      storeApi.org_users.list({ user_id: userId }),
      storeApi.course_users.list({ user_id: userId }),
      storeApi.course_grouping_users.list({ user_id: userId }),
    ]);
  }, [userId]);

  const availableRoles = useAppSelector(selectAvailableHomeRoles);

  // Accessible role encoded in the URL (undefined on bare `/home` or inaccessible role paths).
  const roleFromPath = availableRoles.find((role) =>
    location.pathname.startsWith(homeUrlForRole(role)),
  );
  // UI fallback only — must not be persisted as lastHomeRole (see home layout).
  const currentRole = roleFromPath ?? HomeRole.STUDENT;

  return { currentRole, roleFromPath, availableRoles, isHomeRoleLoading };
};
